import React, { useEffect, useState } from 'react';
import {
  Box, TextField, InputAdornment, Typography, LinearProgress, CircularProgress,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { ENDPOINTS } from '../utils/api.js';

/** Password strength 0–4 with a label + bar color. Simple heuristic (length + variety). */
export function passwordStrength(pw) {
  if (!pw) return { score: 0, label: '', color: 'inherit' };
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score += 1;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) score += 1;
  const label = ['Too short', 'Weak', 'Okay', 'Good', 'Strong'][score];
  const color = ['#d32f2f', '#e65100', '#f9a825', '#2e7d32', '#1b5e20'][score];
  return { score, label, color };
}

/**
 * Username + password + confirm fields with real-time username availability and a
 * password strength indicator. Controlled: the parent owns the values.
 *
 * Props:
 *   username, setUsername, password, setPassword, confirm, setConfirm — controlled state
 *   excludeSelf   – availability check ignores the caller's own current username
 *   required      – when false (optional section), empty fields are valid
 *   onValidity    – (bool) => void, recomputed whenever inputs/availability change
 *   size          – MUI field size (default 'small')
 */
export default function UsernamePasswordFields({
  username, setUsername, password, setPassword, confirm, setConfirm,
  excludeSelf = false, required = false, onValidity = null, size = 'small',
}) {
  const [avail, setAvail] = useState(null);   // null = unknown, {available, message}
  const [checking, setChecking] = useState(false);

  // Debounced real-time availability check. State updates are deferred into the
  // debounce timer to satisfy the strict set-state-in-effect lint rule.
  useEffect(() => {
    const name = (username || '').trim();
    if (name.length < 3) {
      const t0 = setTimeout(() => { setAvail(null); setChecking(false); }, 0);
      return () => clearTimeout(t0);
    }
    const t = setTimeout(async () => {
      setChecking(true);
      try {
        const headers = {};
        const token = localStorage.getItem('accessToken');
        if (excludeSelf && token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(ENDPOINTS.USERNAME_AVAILABLE(name, excludeSelf), { headers });
        const data = await res.json().catch(() => null);
        setAvail(data && typeof data.available === 'boolean' ? data : null);
      } catch { setAvail(null); }
      setChecking(false);
    }, 350);
    return () => clearTimeout(t);
  }, [username, excludeSelf]);

  const strength = passwordStrength(password);
  const usernameFilled = (username || '').trim().length >= 3;
  const usernameOk = usernameFilled && !!(avail && avail.available);
  const passwordOk = password.length >= 8 && password === confirm;
  const empty = !(username || '').trim() && !password && !confirm;
  const valid = required ? (usernameOk && passwordOk) : (empty || (usernameOk && passwordOk));

  useEffect(() => {
    if (!onValidity) return undefined;
    const t = setTimeout(() => onValidity(valid), 0); // deferred: parent setState out of effect body
    return () => clearTimeout(t);
  }, [valid, onValidity]);

  const availabilityHint = () => {
    const name = (username || '').trim();
    if (!name) return required ? 'Choose a username for signing in.' : ' ';
    if (name.length < 3) return 'At least 3 characters.';
    if (checking) return 'Checking availability…';
    if (avail) return avail.available ? '✅ Available' : `❌ ${avail.message || 'Taken'}`;
    return ' ';
  };

  return (
    <Box>
      <TextField
        fullWidth
        size={size}
        label="Username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        error={usernameFilled && avail != null && !avail.available}
        helperText={availabilityHint()}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><PersonOutlineIcon fontSize="small" color="action" /></InputAdornment>
          ),
          endAdornment: checking ? (
            <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
          ) : null,
        }}
        sx={{ mb: 1.5 }}
      />
      <TextField
        fullWidth
        size={size}
        type="password"
        label="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        helperText={password && password.length < 8 ? 'Minimum 8 characters.' : ' '}
        error={!!password && password.length < 8}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><LockOutlinedIcon fontSize="small" color="action" /></InputAdornment>
          ),
        }}
        sx={{ mb: 0.5 }}
      />
      {password && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={(strength.score / 4) * 100}
            sx={{ flexGrow: 1, height: 6, borderRadius: 3,
                  '& .MuiLinearProgress-bar': { bgcolor: strength.color } }}
          />
          <Typography variant="caption" sx={{ color: strength.color, fontWeight: 600, minWidth: 56 }}>
            {strength.label}
          </Typography>
        </Box>
      )}
      <TextField
        fullWidth
        size={size}
        type="password"
        label="Confirm password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        error={!!confirm && confirm !== password}
        helperText={confirm && confirm !== password ? 'Passwords do not match.' : ' '}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><LockOutlinedIcon fontSize="small" color="action" /></InputAdornment>
          ),
        }}
      />
    </Box>
  );
}
